/**
 * Thin wrapper over Supabase Auth.
 *
 * The project runs with "Confirm email" switched off and no SMTP: signing up
 * signs the learner straight in, and Supabase never sends an email. So there is
 * no password reset; learners can change their password while signed in and
 * delete their own account (see supabase/schema.sql, delete_my_account).
 *
 * Every export is safe to call even when accounts aren't configured — they
 * reject with a clear error instead of throwing on a null client, so callers
 * (UI code) don't need to sprinkle `isSupabaseConfigured()` checks everywhere.
 */

import { getSupabase, isSupabaseConfigured } from '../lib/supabaseClient.js';
import { emit, EVENTS } from '../app/eventBus.js';

const NOT_CONFIGURED_ERROR = 'Accounts are not set up for this deployment yet.';

let currentUser = null;
let initialized = false;

async function requireClient() {
  const client = await getSupabase();
  if (!client) throw new Error(NOT_CONFIGURED_ERROR);
  return client;
}

/** Called once from main.js. No-ops entirely when accounts aren't configured. */
export function initAuth() {
  if (initialized || !isSupabaseConfigured()) return;
  initialized = true;

  getSupabase().then((client) => {
    client.auth.getSession().then(({ data }) => {
      currentUser = data.session?.user || null;
      emit(EVENTS.authChanged, { status: currentUser ? 'signed-in' : 'signed-out', user: currentUser });
    });

    client.auth.onAuthStateChange((_event, session) => {
      currentUser = session?.user || null;
      emit(EVENTS.authChanged, { status: currentUser ? 'signed-in' : 'signed-out', user: currentUser });
    });
  });
}

export function getCurrentUser() {
  return currentUser;
}

export async function signUp({ email, password }) {
  const client = await requireClient();
  const { data, error } = await client.auth.signUp({ email, password });
  if (error) throw error;
  return data;
}

export async function signIn({ email, password }) {
  const client = await requireClient();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

/** Change the password of the signed-in learner (there is no email-based reset). */
export async function changePassword(password) {
  const client = await requireClient();
  const { error } = await client.auth.updateUser({ password });
  if (error) throw error;
}

/** Permanently delete the signed-in learner's account and synced progress, then sign out. */
export async function deleteAccount() {
  const client = await requireClient();
  const { error } = await client.rpc('delete_my_account');
  if (error) throw error;
  await client.auth.signOut({ scope: 'local' });
}

export async function signOut() {
  const client = await requireClient();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  if (!isSupabaseConfigured()) return null;
  const { data } = await (await getSupabase()).auth.getSession();
  return data.session;
}
